import React, { useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

export const MarkdownPdfView: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markdown, setMarkdown] = useState<string>('');

  const handleExtract = async () => {
    if (!file) return;
    setIsProcessing(true);
    setError(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        
        let lastY = -1;
        let pageText = '';
        
        for (const item of textContent.items) {
          if ('transform' in item) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const y = item.transform[5];
            if (lastY !== -1 && Math.abs(y - lastY) > 5) {
              pageText += '\n';
            }
            pageText += item.str + ' ';
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            lastY = y;
          }
        }
        fullText += `## Page ${i}\n\n${pageText}\n\n---\n\n`;
      }
      setMarkdown(fullText);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    void navigator.clipboard.writeText(markdown);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Extract to Markdown</h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--color-text-secondary)' }}>Extract raw text blocks and paragraphs into Markdown format.</p>
        </div>
        <button onClick={() => { void handleExtract(); }} disabled={isProcessing || !file} style={{ padding: '0.5rem 1rem', background: 'var(--color-accent)', color: 'var(--color-on-accent)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
          {isProcessing ? 'Extracting...' : 'Extract Text'}
        </button>
      </div>
      {error && <div style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem', background: '#ffebee', borderRadius: '4px' }}>{error}</div>}
      
      <div style={{ flex: 1, display: 'flex', gap: '1rem', height: '100%' }}>
        <div style={{ flex: 1, border: '2px dashed var(--color-border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <input 
            type="file" 
            accept="application/pdf" 
            onChange={(e) => {
              const file = e.target.files?.[0];
            if (file) {
                setFile(file);
                setError(null);
              }
            }}
            style={{ marginBottom: '1rem' }}
          />
          {file && <span>{file.name}</span>}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <textarea
            readOnly
            value={markdown}
            placeholder="Markdown output will appear here..."
            style={{ flex: 1, padding: '1rem', borderRadius: '8px', border: '1px solid var(--color-border)', resize: 'none', fontFamily: 'monospace' }}
          />
          <button onClick={handleCopy} disabled={!markdown} style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: 'pointer' }}>
            Copy to Clipboard
          </button>
        </div>
      </div>
    </div>
  );
};
